import { apiClient, skipAuthRefresh } from './client'
import { ApiError } from './errors'
import type { AuthSession, ImpersonationSession, UserProfile } from '@/types/domain'

const http = () => apiClient.axios

/**
 * Accepts either shape `/v1/auth/me` and the profile endpoints answer with:
 * the gateway's `models.User` (`uid`, one `name`, `role`, `is_active`) or the
 * richer `id`/`firstName`/`displayName` profile. Missing fields become empty
 * rather than undefined, so a caller never has to guard a name it will print.
 */
export function normalizeUser(raw: Record<string, unknown>): UserProfile {
  const name = raw.name as string | undefined
  return {
    id: (raw.id as string) ?? (raw.uid as string) ?? '',
    email: (raw.email as string) ?? '',
    displayName: (raw.displayName as string) ?? name ?? '',
    firstName: (raw.firstName as string) ?? name,
    lastName: raw.lastName as string | undefined,
    role: raw.role as UserProfile['role'],
    isActive: (raw.is_active as boolean) ?? (raw.isActive as boolean),
    emailVerified: raw.emailVerified as boolean | undefined,
    onboarded: raw.onboarded as boolean | undefined,
    defaultFolder: (raw.defaultFolder as string) ?? '',
    profilePicture: raw.profilePicture as string | undefined,
    folders: (raw.folders as string[]) ?? [],
    recentDocuments: (raw.recentDocuments as string[]) ?? [],
  }
}

/**
 * Sign-in, registration and the OAuth round trip. Ported from the Flutter
 * app's `lib/services/auth/auth_client.dart`.
 *
 * The access token is returned to the caller and held by `apiClient` in
 * memory; it is never persisted. `refresh()` exchanges the http-only cookie
 * for a new one on every app start.
 */
export const authApi = {
  async login(email: string, password: string): Promise<string> {
    const { data } = await http().post<AuthSession>(
      '/v1/auth/login',
      { email, password },
      skipAuthRefresh,
    )
    return data.access_token
  },

  async register(input: {
    email: string
    password: string
    firstName: string
    lastName?: string
  }): Promise<void> {
    // The gateway requires a non-empty firstName; fall back to the email's
    // local part so registration never fails validation on a blank one.
    const firstName = input.firstName.trim() || input.email.split('@')[0]
    await http().post(
      '/v1/auth/register',
      {
        email: input.email,
        password: input.password,
        firstName,
        lastName: (input.lastName ?? '').trim(),
      },
      skipAuthRefresh,
    )
  },

  async logout(): Promise<void> {
    try {
      await http().post('/v1/auth/logout')
    } catch {
      // Signing out locally must succeed even if the server call does not.
    }
  },

  /**
   * Exchanges the refresh cookie for an access token.
   *
   * Single-flight is enforced one level up, in `apiClient`: the gateway
   * rotates the refresh token on every call and treats a second call with
   * the old cookie as reuse, which revokes the session. Never call this
   * concurrently with itself.
   */
  async refresh(): Promise<string | null> {
    try {
      const { data } = await http().post<AuthSession>('/v1/auth/refresh', null, skipAuthRefresh)
      return data.access_token ?? null
    } catch {
      return null
    }
  },

  async me(): Promise<UserProfile | null> {
    try {
      const { data } = await http().get<Record<string, unknown>>('/v1/auth/me')
      return normalizeUser(data)
    } catch {
      return null
    }
  },

  /**
   * Hands a Google ID token to the gateway, which verifies it against
   * `accounts.google.com` and answers with a session.
   *
   * The token must come from Google Identity Services, not from Firebase
   * Auth: `internal/auth/oauth/google.go` rejects any issuer that is not
   * `accounts.google.com`, and a Firebase token's issuer is
   * `securetoken.google.com`.
   */
  async signInWithGoogle(idToken: string): Promise<string> {
    const { data } = await http().post<AuthSession>(
      '/v1/auth/oauth/google',
      { id_token: idToken },
      skipAuthRefresh,
    )
    return data.access_token
  },

  /**
   * Trades the one-time code the gateway's OAuth callback handed over for a
   * session. The code is single-use and expires after a minute; the refresh
   * token arrives as an http-only cookie, as it does for a password login.
   * No provider token and no session token ever travels in a URL.
   */
  async exchangeOAuthCode(code: string): Promise<string> {
    const { data } = await http().post<AuthSession>(
      '/v1/auth/oauth/exchange',
      { code },
      skipAuthRefresh,
    )
    return data.access_token
  },

  /**
   * Which third-party sign-ins this deployment has configured, so the login
   * screen only offers buttons that can work. An unreachable gateway yields
   * an empty list rather than an error: the password form still works.
   */
  async oauthProviders(): Promise<string[]> {
    try {
      const { data } = await http().get<{ providers?: unknown }>(
        '/v1/auth/oauth/providers',
        skipAuthRefresh,
      )
      return Array.isArray(data.providers)
        ? data.providers.filter((p): p is string => typeof p === 'string')
        : []
    } catch {
      return []
    }
  },

  /**
   * Where to send the browser for a redirect-flow provider (Microsoft
   * today; Google too if it ever becomes a `RedirectProvider`).
   *
   * `returnTo` must be a path on this origin — the gateway drops anything
   * that is not (`sanitizeReturnTo`: leading `/`, not `//`, no CR/LF), so a
   * full URL here silently loses the destination.
   */
  startUrl(provider: string, returnTo?: string): string {
    const base = `${apiClient.axios.defaults.baseURL ?? ''}/v1/auth/oauth/${provider}/start`
    if (!returnTo) return base
    return `${base}?returnTo=${encodeURIComponent(returnTo)}`
  },

  async requestPasswordReset(email: string): Promise<void> {
    await http().post('/v1/auth/password-reset/request', { email }, skipAuthRefresh)
  },

  async confirmPasswordReset(token: string, password: string): Promise<void> {
    await http().post('/v1/auth/password-reset/confirm', { token, password }, skipAuthRefresh)
  },

  async resendVerification(): Promise<void> {
    await http().post('/v1/auth/resend-verification')
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await http().post('/v1/auth/change-password', { currentPassword, newPassword })
  },

  async onboard(payload: Record<string, unknown>): Promise<void> {
    await http().post('/v1/auth/onboard', payload)
  },

  /**
   * Trades a staff member's one-time impersonation code for a read-only
   * session — `/auth/impersonate#code=…`, where the gateway's
   * `POST /v1/staff/users/:uid/impersonate` sends the browser. Unlike every
   * other exchange here, the answer carries the full session (user,
   * `impersonatedBy`, `expiresAt`) because there is no `/v1/auth/me` call to
   * follow it up with: this token gets no refresh and must not touch the
   * refresh cookie, so `AuthProvider.beginImpersonation` takes the whole
   * shape in one round trip rather than one call for the token and another
   * for the profile.
   */
  async exchangeImpersonation(code: string): Promise<ImpersonationSession> {
    const { data } = await http().post<{
      accessToken: string
      expiresIn: number
      expiresAt: string
      user: Record<string, unknown>
      impersonatedBy: { uid: string; email: string; name?: string }
    }>('/v1/auth/impersonation/exchange', { code }, skipAuthRefresh)
    return {
      accessToken: data.accessToken,
      expiresIn: data.expiresIn,
      expiresAt: data.expiresAt,
      user: normalizeUser(data.user),
      impersonatedBy: data.impersonatedBy,
    }
  },
}

export { ApiError }
